import { useEffect, useState, useRef } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Search, MapPin, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

// Fix for default marker icon in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icon
const customIcon = new L.Icon({
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Component to handle map click events
const MapClickHandler = ({ onLocationSelect }) => {
  useMapEvents({
    click: (e) => {
      onLocationSelect(e.latlng);
    },
  });
  return null;
};

// Component to recenter map
const RecenterMap = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, 15);
    }
  }, [center, map]);
  return null;
};

const MapPicker = ({ 
  initialPosition = null, 
  onLocationSelect, 
  onAddressSelect,
  height = '300px',
  showSearch = true 
}) => {
  // Default to Mumbai coordinates
  const defaultCenter = [19.0760, 72.8777];
  const [position, setPosition] = useState(initialPosition || defaultCenter);
  const [markerPosition, setMarkerPosition] = useState(initialPosition);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [reverseGeocoding, setReverseGeocoding] = useState(false);
  const searchTimeoutRef = useRef(null);

  // Update position when initialPosition changes
  useEffect(() => {
    if (initialPosition) {
      setPosition(initialPosition);
      setMarkerPosition(initialPosition);
    }
  }, [initialPosition]);

  // Handle map click
  const handleMapClick = async (latlng) => {
    const newPos = [latlng.lat, latlng.lng];
    setMarkerPosition(newPos);
    setPosition(newPos);
    
    if (onLocationSelect) {
      onLocationSelect({ lat: latlng.lat, lng: latlng.lng });
    }

    // Reverse geocode to get address
    await reverseGeocode(latlng.lat, latlng.lng);
  };

  // Reverse geocode using Nominatim
  const reverseGeocode = async (lat, lng) => {
    setReverseGeocoding(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`,
        { headers: { 'Accept-Language': 'en' } }
      );
      const data = await response.json();
      
      if (data && data.address && onAddressSelect) {
        const addr = data.address;
        onAddressSelect({
          address_line1: [addr.house_number, addr.road].filter(Boolean).join(' ') || addr.neighbourhood || '',
          address_line2: addr.suburb || addr.neighbourhood || '',
          city: addr.city || addr.town || addr.village || addr.county || '',
          state: addr.state || '',
          pincode: addr.postcode || '',
          country: addr.country || '',
          display_name: data.display_name,
          lat,
          lng
        });
      }
    } catch (error) {
      console.error('Reverse geocoding error:', error);
    } finally {
      setReverseGeocoding(false);
    }
  };

  // Search places using Nominatim
  const searchPlaces = async (query) => {
    if (!query || query.length < 3) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&countrycodes=in&limit=5&addressdetails=1`,
        { headers: { 'Accept-Language': 'en' } }
      );
      const data = await response.json();
      setSearchResults(data);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  // Debounced search
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchPlaces(query);
    }, 500);
  };

  // Handle search result selection
  const handleResultSelect = (result) => {
    const newPos = [parseFloat(result.lat), parseFloat(result.lon)];
    setPosition(newPos);
    setMarkerPosition(newPos);
    setSearchQuery(result.display_name);
    setSearchResults([]);

    if (onLocationSelect) {
      onLocationSelect({ lat: parseFloat(result.lat), lng: parseFloat(result.lon) });
    }

    if (onAddressSelect && result.address) {
      const addr = result.address;
      onAddressSelect({
        address_line1: [addr.house_number, addr.road].filter(Boolean).join(' ') || addr.neighbourhood || '',
        address_line2: addr.suburb || addr.neighbourhood || '',
        city: addr.city || addr.town || addr.village || addr.county || '',
        state: addr.state || '',
        pincode: addr.postcode || '',
        country: addr.country || '',
        display_name: result.display_name,
        lat: parseFloat(result.lat),
        lng: parseFloat(result.lon)
      });
    }
  };

  return (
    <div className="space-y-3">
      {/* Search Box */}
      {showSearch && (
        <div className="relative">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Search for a location..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="pl-10 pr-10"
            />
            {searching && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin" />
            )}
          </div>

          {/* Search Results Dropdown */}
          {searchResults.length > 0 && (
            <div className="absolute z-[1000] w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {searchResults.map((result, index) => (
                <button
                  key={result.place_id || `result-${index}-${result.display_name?.slice(0, 30)}`}
                  type="button"
                  onClick={() => handleResultSelect(result)}
                  className="w-full px-4 py-3 text-left hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
                >
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-[#0047AB] mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-slate-700 line-clamp-2">{result.display_name}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Map */}
      <div className="relative rounded-xl overflow-hidden border border-slate-200" style={{ height }}>
        <MapContainer
          center={position}
          zoom={13}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapClickHandler onLocationSelect={handleMapClick} />
          <RecenterMap center={position} />
          {markerPosition && (
            <Marker position={markerPosition} icon={customIcon} />
          )}
        </MapContainer>

        {/* Loading overlay for reverse geocoding */}
        {reverseGeocoding && (
          <div className="absolute inset-0 bg-white/50 flex items-center justify-center z-[1000]">
            <div className="bg-white rounded-lg px-4 py-2 shadow-lg flex items-center gap-2">
              <Loader2 className="w-4 h-4 text-[#0047AB] animate-spin" />
              <span className="text-sm text-slate-600">Getting address...</span>
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="absolute bottom-2 left-2 right-2 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 text-xs text-slate-600 z-[500]">
          <MapPin className="w-3 h-3 inline mr-1 text-[#0047AB]" />
          Click on the map to select your delivery location
        </div>
      </div>
    </div>
  );
};

export default MapPicker;
